import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import { useModalConfirmation, useQueryData, useToast } from "hooks";
import { GET_CONNECTION } from "graphql/queries";
import { UPDATE_CONNECTION, REMOVE_CALENDLY_CONNECTION } from "graphql/mutations";

/**
 * useConnection
 *
 * @param {String}  id
 * @param {String}  type
 * @param {String}  recordType
 * @param {String}  recordId
 * @param {Boolean} shouldSkip
 */
export default function useConnection({ id, type, recordType, recordId, shouldSkip }) {
  const { show } = useModalConfirmation();
  const { toast } = useToast();
  const [data, setData] = useState();

  const [handleUpdateConnection, { loading: updating }] = useMutation(UPDATE_CONNECTION);
  const [handleRemoveCalendlyConnection, { loading: removingCalendlyConnection }] =
    useMutation(REMOVE_CALENDLY_CONNECTION);

  const {
    loading,
    data: response,
    error,
  } = useQueryData({
    queryName: GET_CONNECTION,
    keyName: "connection",
    skip: shouldSkip,
    variables: { id, type, recordType, recordId },
  });

  useEffect(() => {
    if (response) {
      setData(response.connection);
    }
  }, [response]);

  const updateConnection = (id, params) => {
    handleUpdateConnection({
      variables: {
        input: {
          id,
          params,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useConnection.UpdateSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useConnection.UpdateError" />),
    });
  };

  const removeCalendlyConnection = async (id, refetchQueries = []) => {
    const confirmed = await show(
      <FormattedMessage id="useConnection.RemoveCalendlyConfirmation.Title" />,
      <FormattedMessage id="useConnection.RemoveCalendlyConfirmation.Description" />,
      {
        primaryButtonMessageId: "Global.Confirm",
        showSaveAsDestructable: true,
        showSave: false,
      }
    );

    if (!confirmed) return;

    handleRemoveCalendlyConnection({
      variables: {
        input: {
          id,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useConnection.RemoveCalendlySuccess" />),
      onError: () => toast.error(<FormattedMessage id="useConnection.RemoveCalendlyError" />),
      refetchQueries,
    });
  };

  return {
    loading,
    data,
    error,
    updating,
    removingCalendlyConnection,
    updateConnection,
    removeCalendlyConnection,
  };
}
